import React, { FC, ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { LuDatabase, LuRotateCcw } from 'react-icons/lu';
import { ConsoleAPIGraph, CreativeHubTypes } from '@adflow/graphqlgen';
import {
  AdElement,
  AdTemplate,
  AdflowAPI,
  EditorSidebarButtonProps,
  EditorState,
} from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import {
  Box,
  Button,
  Flex,
  FocusLock,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { EditorStoreContext } from '../../';
import { useStore } from '../../hooks/useStore';
import { useTemplateAnimationsFeature } from '../../hooks/useTemplateAnimationsFeature';
import { useTemplateEditorCanvasFeature } from '../../hooks/useTemplateEditorCanvasFeature';
import {
  EditorStageViewProvider,
  isCanvasEditorStage,
  type EditorStageView,
} from '../../contexts/EditorStageViewContext';
import LeftSidebar from '../LeftSidebar';
import { getSourceConfigsForImport } from './sourceConfigs';
import Rightsidebar from '../Rightsidebar';
import Toolbox from '../Toolbox';
import Topbar from '../Topbar';
import MultiDataSourcesForm from './MultiDataSourcesForm';
import { EditorCenterStage } from './EditorCenterStage';

type Module = {
  name: string;
  EditorSidebarButton: FC<EditorSidebarButtonProps>;
  EditorSidebarView?: FC;
};

const ANIMATION_PLAYBACK_FOOTER_HEIGHT = '56px';

interface Props {
  modules: Array<Module>;
  onSave?: (ctx: EditorState) => Promise<void>;
  renderPreview?: (args: RenderPreviewArgs) => ReactElement;
  renderTopbar?: () => ReactElement;
  saveButtonVisible?: boolean;
  isEditMode?: boolean;
}

type RenderPreviewArgs = {
  elements: Array<AdElement>;
  importSourceJSON?: string;
  importSourceType?: AdTemplate['importSourceType'];
  brand: ConsoleAPIGraph.Brand | null;
  brandKit?: CreativeHubTypes.Brand | null;
  fonts: Array<{
    fontFamily: string;
    fontFamilyId?: string;
    weight: number;
    style: string;
    stretch?: string;
    src: string;
  }>;
  displayOptions?: AdflowAPI.AdTemplateDisplayOptions;
  selectedElementId: string | null;
  isIndexPreview: boolean;
  dataSources?: Array<{ id: string; type: string }>;
  animation?: AdflowAPI.Animation | null;
  animationPlaybackToken?: number;
  preResolvedElements?: AdElement[] | null;
  preResolvedAnimationOverrides?: ReadonlyMap<string, ElementAnimationStyle> | null;
};

const Editor: FC<Props> = ({
  modules,
  renderPreview,
  renderTopbar,
  onSave,
  saveButtonVisible = true,
  isEditMode = false,
}) => {
  const ctx = useContext(EditorStoreContext);

  const [searchParams] = useSearchParams();
  const templateTypeParam = searchParams.get('templateType');

  const [activeModule, setActiveModule] = useState<string>(
    templateTypeParam ? templateTypeParam.charAt(0).toUpperCase() + templateTypeParam.slice(1) : '',
  );

  const {
    elements,
    brand,
    brandKit,
    fonts,
    displayOptions,
    selectedElementId,
    importSourceJSON,
    importSourceType,
    dataSources,
    animation,
    animationPlaybackToken,
  } = useStore((state) => ({
    brand: state.brand,
    brandKit: state.brandKit,
    elements: state.elements,
    fonts: state.customFonts,
    displayOptions: state.displayOptions,
    selectedElementId: state.selectedElementId,
    importSourceJSON: state.importSourceJSON,
    importSourceType: state.importSourceType,
    dataSources: state.dataSources,
    animation: state.animation,
    animationPlaybackToken: state.animationPlaybackToken,
  }));

  // Creatomate video: only Soccer Sports Moments (no NBA)
  const filteredSourceConfigs = useMemo(
    () => getSourceConfigsForImport(importSourceType),
    [importSourceType],
  );

  const isIndexPreview = false;
  // const ActiveEditorSidebarView = useMemo(() => {
  //   return modules.find(module => module.name === activeModule)
  //     ?.EditorSidebarView;
  // }, [activeModule, modules]);

  // Reference to the store is stable between renders. As we need
  // access to the full editor state it's best to use this here to
  // avoid unnecessary re-renders. useStore() is good if you want
  // to subscribe to a _part_ of the store state.
  const handleOnSave = useCallback(async () => {
    if (ctx.store != null && onSave != null) {
      await onSave(ctx.store.getState());
    }
  }, [ctx.store, onSave]);

  // const selectedElement = useEditorStore(s => s.selectedElementId);
  const s = useStore((state) => ({
    setSelectedElement: state.setElementSelected,
    replayAnimation: state.replayAnimation,
  }));

  const handleClick = useCallback(() => {
    s.setSelectedElement(null);
  }, [s]);

  const animationsEnabled = useTemplateAnimationsFeature();
  const showReplayControls = animationsEnabled && (animation?.effects?.length ?? 0) > 0;

  const canvasSupported = importSourceType !== 'creatomate';
  const canvasFeatureEnabled = useTemplateEditorCanvasFeature();
  const showCanvasToggle = canvasFeatureEnabled && canvasSupported;
  const [stageView, setStageView] = useState<EditorStageView>('preview');
  const isEditorStage = isCanvasEditorStage(showCanvasToggle, stageView);

  const previewArgs: RenderPreviewArgs = {
    elements,
    brand,
    brandKit,
    fonts,
    displayOptions,
    selectedElementId,
    isIndexPreview,
    importSourceJSON,
    importSourceType,
    dataSources,
    animation: animationsEnabled ? animation : null,
    animationPlaybackToken: animationsEnabled ? animationPlaybackToken : 0,
  };

  return (
    <Flex
      flex={1}
      width="100%"
      height="100%"
      direction="column"
      minH={0}
      overflow="hidden"
      data-test-id="animation-feature-state"
      data-animations-enabled={animationsEnabled ? 'true' : 'false'}
    >
      {renderTopbar ? (
        renderTopbar()
      ) : (
        <Topbar
          onSave={handleOnSave}
          saveButtonVisible={saveButtonVisible}
          sources={filteredSourceConfigs}
        />
      )}
      {/* fill full height - height of toolbar */}
      <EditorStageViewProvider
        value={{
          stageView: showCanvasToggle ? stageView : 'preview',
          isEditorStage,
          showCanvasToggle,
          setStageView,
        }}
      >
        <Flex flex={1} minH={0} overflow="hidden">
          <Toolbox>
            {modules.map((M) => (
              <M.EditorSidebarButton
                key={M.name}
                openSidebarView={(value) => {
                  setActiveModule(value);
                }}
                selected={activeModule === M.name}
                disableAdvertiserSelection={isEditMode}
              ></M.EditorSidebarButton>
            ))}
            <DataSourceBtn />
          </Toolbox>
          <LeftSidebar />
          <Flex
            flex={1}
            minW={0}
            minH={0}
            position="relative"
            onClick={handleClick}
            direction="column"
            overflow="hidden"
          >
            <Flex flex={1} minH={0} minW={0} width="100%" overflow="hidden" direction="column">
              <Box
                flex={1}
                minH={0}
                minW={0}
                width="100%"
                display="flex"
                flexDirection="column"
                onClick={(e) => e.stopPropagation()}
              >
                {renderPreview ? (
                  <EditorCenterStage renderPreview={renderPreview} previewArgs={previewArgs} />
                ) : null}
              </Box>
            </Flex>
            {animationsEnabled && (
              <Flex
                flexShrink={0}
                height={ANIMATION_PLAYBACK_FOOTER_HEIGHT}
                alignItems="center"
                gap={3}
                px={4}
                py={3}
                onClick={(e) => e.stopPropagation()}
                data-test-id="animation-playback-footer"
              >
                {showReplayControls && (
                  <Button
                    size="sm"
                    colorScheme="green"
                    leftIcon={<Icon as={LuRotateCcw} />}
                    onClick={s.replayAnimation}
                    data-test-id="animation-replay-button"
                  >
                    Replay
                  </Button>
                )}
              </Flex>
            )}
          </Flex>
          <Rightsidebar />
        </Flex>
      </EditorStageViewProvider>
    </Flex>
  );
};

Editor.displayName = 'Editor';

export type { SourceConfig } from './sourceConfigs';

export default Editor;
export type { Props as EditorProps };
export type { RenderPreviewArgs };
const DataSourceBtn: FC<Record<string, unknown>> = () => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const firstFieldRef = React.useRef(null);
  const importSourceType = useStore((state) => state.importSourceType);

  const filteredSources = useMemo(
    () => getSourceConfigsForImport(importSourceType),
    [importSourceType],
  );

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      initialFocusRef={firstFieldRef}
      onOpen={onOpen}
      onClose={onClose}
      placement="right"
      closeOnBlur={true}
    >
      <PopoverTrigger>
        <IconButton
          size="lg"
          px={4}
          aria-label="Search database"
          icon={<Icon as={LuDatabase} />}
          borderRadius="100%"
          aspectRatio="1/1"
          data-test-id="data-sources-button"
        />
      </PopoverTrigger>
      <PopoverContent w={500} p={5}>
        <FocusLock restoreFocus persistentFocus={false}>
          <PopoverArrow />
          <PopoverCloseButton />
          <MultiDataSourcesForm
            sources={filteredSources}
            firstFieldRef={firstFieldRef}
            onCancel={onClose}
          ></MultiDataSourcesForm>
        </FocusLock>
      </PopoverContent>
    </Popover>
  );
};
